import { offset, shift } from "@floating-ui/dom";
import { autoUpdate, useFloating } from "@floating-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilterCircleXmark } from "@fortawesome/sharp-light-svg-icons";
import { Popover, Portal } from "@headlessui/react";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import type { FilterState } from "./state";

// Synthetic filter key for a table's free-text search box, which isn't tied to a
// column. Tables that pass a `search` string to useActiveFilters must handle this key
// in their onClearFilter callback (by clearing the search input).
export const SEARCH_FILTER_KEY = "__search__";

export interface ActiveFilter {
    key: string;
    label: string;
}

const isFilterValueActive = (value: unknown): boolean => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value !== "";
    return true;
};

const columnLabel = <T,>(
    column: ColumnDef<T, any>,
    fallback: string,
): string => {
    const header = column.header;
    if (typeof header === "string") return header;
    const resolved = (header as any)?.();
    return typeof resolved === "string" ? resolved : fallback;
};

interface UseActiveFiltersArgs<T> {
    columns: ColumnDef<T, any>[];
    /** Current table filters (tableState.filters). */
    filters: FilterState;
    /** Filter keys that are never shown as chips (e.g. internal, non-user filters). */
    ignoreKeys?: string[];
    /** Free-text search value; adds a "Search" chip when non-empty. */
    search?: string;
}

// Computes the list of filters the user has actively set.
export const useActiveFilters = <T,>({
    columns,
    filters,
    ignoreKeys,
    search,
}: UseActiveFiltersArgs<T>): ActiveFilter[] => {
    // Map each filter key -> human-readable label. The filter key is
    // `meta.filteringKey ?? column id` (the same resolution TableFilter uses).
    const filterKeyLabels = useMemo(() => {
        const labels: Record<string, string> = {};
        for (const column of columns) {
            const id = column.id ?? (column as any).accessorKey;
            if (!id) continue;
            const key = column.meta?.filteringKey ?? id;
            labels[key] = columnLabel(column, id);
        }
        return labels;
    }, [columns]);

    return useMemo(() => {
        const result: ActiveFilter[] = [];

        if (search && search.trim() !== "") {
            result.push({ key: SEARCH_FILTER_KEY, label: "Search" });
        }

        for (const [key, value] of Object.entries(filters)) {
            if (!isFilterValueActive(value)) continue;
            if (ignoreKeys?.includes(key)) continue;
            result.push({ key, label: filterKeyLabels[key] ?? key });
        }

        return result;
    }, [filters, ignoreKeys, filterKeyLabels, search]);
};

interface ClearFiltersButtonProps {
    activeFilters: ActiveFilter[];
    /** Clear a single filter by key (handles SEARCH_FILTER_KEY for the search box). */
    onClearFilter: (key: string) => void;
    /** Clear every filter at once (the table's reset). */
    onClearAll: () => void;
}

export const ClearFiltersButton = ({
    activeFilters,
    onClearFilter,
    onClearAll,
}: ClearFiltersButtonProps) => {
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [offset({ mainAxis: 4 }), shift()],
        whileElementsMounted: autoUpdate,
    });

    const disabled = activeFilters.length === 0;

    return (
        <div
            ref={refs.setReference}
            className={`h-7 flex items-stretch rounded border text-sm font-medium overflow-hidden ${
                disabled
                    ? "border-ae-gray-250 text-gray-300"
                    : "border-ae-gray-250 text-ae-blue-900"
            }`}
        >
            <button
                type="button"
                disabled={disabled}
                onClick={onClearAll}
                className={`flex items-center gap-1.5 px-2 focus:outline-none ${
                    disabled ? "cursor-default" : "hover:bg-ae-gray-100"
                }`}
            >
                <FontAwesomeIcon icon={faFilterCircleXmark} className="w-4" />
                Clear filters
                {!disabled && (
                    <span className="min-w-4 h-4 px-1.5 inline-flex items-center justify-center text-xs font-bold text-white bg-ae-blue-550 rounded-full">
                        {activeFilters.length}
                    </span>
                )}
            </button>
            <Popover>
                <Popover.Button
                    disabled={disabled}
                    title="Show active filters"
                    className={`flex items-center px-1.5 h-full border-l border-ae-gray-250 focus:outline-none ${
                        disabled ? "cursor-default" : "hover:bg-ae-gray-100"
                    }`}
                >
                    <ChevronDownIcon className="w-3.5" />
                </Popover.Button>
                <Portal>
                    <Popover.Panel
                        ref={refs.setFloating}
                        style={floatingStyles}
                        className="min-w-56 max-w-80 rounded-md p-2 border-2 border-ae-slate-300 shadow-lg shadow-neutral-200 z-50 bg-white"
                    >
                        <p className="text-xs font-bold text-gray-500 uppercase px-1 pb-2">
                            Active filters
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {activeFilters.map((filter) => (
                                <button
                                    key={filter.key}
                                    type="button"
                                    onClick={() => onClearFilter(filter.key)}
                                    title={`Remove ${filter.label} filter`}
                                    className="flex items-center gap-1 px-2 py-0.5 text-sm text-ae-blue-900 bg-ae-gray-100 hover:bg-ae-gray-200 border border-ae-gray-250 rounded-full"
                                >
                                    {filter.label}
                                    <XMarkIcon className="w-3.5 h-3.5" />
                                </button>
                            ))}
                        </div>
                    </Popover.Panel>
                </Portal>
            </Popover>
        </div>
    );
};
